import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useCurrency } from '@/contexts/CurrencyContext';
import { ChevronDown, Check, Coins } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Props {
  variant?: 'hero' | 'header';
  className?: string;
}

const OPTIONS = [
  { code: 'ETB' as const, symbol: 'Br', label: 'Ethiopian Birr', sub: 'ETB' },
  { code: 'USD' as const, symbol: '$', label: 'US Dollar', sub: 'USD' },
];

export function CurrencySwitcher({ variant = 'header', className }: Props) {
  const { displayCurrency, setDisplayCurrency, usdToEtb } = useCurrency();
  const current = OPTIONS.find(o => o.code === displayCurrency)!;

  const triggerCls =
    variant === 'hero'
      ? 'inline-flex items-center gap-1 text-[10px] sm:text-xs text-primary hover:underline font-medium'
      : 'inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-foreground/80 hover:text-primary hover:bg-primary/5 transition-colors';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={cn(triggerCls, className)} aria-label="Change currency">
          <Coins className="h-3 w-3" />
          <span>{current.symbol} {current.sub}</span>
          <ChevronDown className="h-3 w-3 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2">
        <div className="px-2 py-1.5 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
          Display currency
        </div>
        {OPTIONS.map(opt => {
          const active = opt.code === displayCurrency;
          return (
            <button
              key={opt.code}
              type="button"
              onClick={() => setDisplayCurrency(opt.code)}
              className={cn(
                'w-full flex items-center justify-between gap-3 px-2 py-2 rounded-md text-left text-sm hover:bg-muted transition-colors',
                active && 'bg-primary/5',
              )}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="h-7 w-7 rounded-md bg-primary/10 text-primary font-bold flex items-center justify-center text-sm shrink-0">
                  {opt.symbol}
                </span>
                <div className="min-w-0">
                  <div className="font-medium leading-tight">{opt.label}</div>
                  <div className="text-[11px] text-muted-foreground leading-tight">{opt.sub}</div>
                </div>
              </div>
              {active && <Check className="h-4 w-4 text-primary shrink-0" />}
            </button>
          );
        })}
        <div className="mt-1 px-2 py-2 border-t text-[11px] text-muted-foreground">
          Rate: <span className="font-semibold text-foreground">1 USD ≈ Br {usdToEtb.toFixed(2)}</span>
          <div className="mt-0.5">Listings stay in their original currency in the database — only the displayed value is converted.</div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
